import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cx } from '../../lib/util/cx';

/**
 * Port of the DS `Modal` (components/feedback/Modal.jsx), portaled to <body>.
 *
 * Handoff rules: backdrop click closes, a click on the card does not; the
 * 32px close button presses to scale(.95); max-height 90vh with internal
 * scroll; sizes sm 352 / md 448 / lg 512 / xl 720 / 2xl 810. Escape also closes, focus
 * moves into the dialog on open and returns to the opener on close.
 *
 * `dismissible={false}` turns all three off (backdrop, Escape, the close
 * button) for a long form where a stray click would lose the work: only the
 * dialog's own buttons close it.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-[352px]',
  md: 'max-w-[448px]',
  lg: 'max-w-[512px]',
  xl: 'max-w-[720px]',
  '2xl': 'max-w-[810px]',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  title?: ReactNode;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  /** False: only the dialog's own buttons close it — no backdrop click, Escape or close button. */
  dismissible?: boolean;
  /** Stacking layer; the global error modal sits above everything else. */
  layer?: 'default' | 'top';
  className?: string;
  children: ReactNode;
}

/** Open dialogs, oldest first. */
const openStack: object[] = [];

export function Modal({
  isOpen,
  onClose,
  size = 'sm',
  title,
  footer,
  hideCloseButton = false,
  dismissible = true,
  layer = 'default',
  className,
  children,
}: ModalProps) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  useEffect(() => {
    onCloseRef.current = onClose;
    dismissibleRef.current = dismissible;
  }, [onClose, dismissible]);

  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    cardRef.current?.focus({ preventScroll: true });
    // Dialogs stack (a dish over a chef, an order over a dish): Escape closes
    // only the one on top.
    const token = {};
    openStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissibleRef.current && openStack[openStack.length - 1] === token)
        onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      openStack.splice(openStack.indexOf(token), 1);
      opener?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Backdrop click closes; a click on the card doesn't (target check, not
  // stopPropagation). Keyboard users close with Escape (document listener
  // above), so the backdrop needs no key handler of its own. The outer
  // stopPropagation keeps clicks from bubbling through the React tree to
  // whatever rendered the modal — the portal escapes the DOM, not React.
  return createPortal(
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      role="presentation"
      className={cx('fixed inset-0 font-body', layer === 'top' ? 'z-[2000]' : 'z-[100]')}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-[rgba(0,0,0,.4)]" aria-hidden="true" />
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        role="presentation"
        className="absolute inset-0 flex items-center justify-center px-3 desk:px-6"
        onClick={(e) => {
          if (dismissible && e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={cx(
            'relative max-h-[90vh] w-full overflow-y-auto rounded-app-card bg-white p-4 text-ink shadow-lg outline-none desk:p-6',
            SIZE[size],
            className,
          )}
        >
          {!hideCloseButton && dismissible && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-muted transition-[transform,background-color] duration-fast hover:bg-[rgba(42,22,9,.05)] active:scale-95"
            >
              <X size={20} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
          {title && (
            <h3 id={titleId} className="mb-3 pr-8 font-heading text-[19px] font-bold tracking-[-.01em] text-brown-deep">
              {title}
            </h3>
          )}
          {children}
          {footer && <div className="mt-5 flex justify-end gap-2.5">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
